import PropTypes from 'prop-types';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Collapse, List, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

// project imports
import NavItem from '../NavItem';
import NavCollapse from '../NavCollapse';

// ==============================|| SIDEBAR MENU LIST GROUP ||============================== //

const NavGroup = ({
  item,
  open = true,
  collapsible = false,
  onToggle,
  dragHandlers,
  isDragging,
  isDropTarget,
  orderedChildren,
  itemDrag
}) => {
  const theme = useTheme();

  // The caller may hand back a reordered copy of item.children. Falling back to
  // the raw list keeps this component usable by the non-collapsible callers,
  // which do not reorder anything.
  const childList = orderedChildren || item.children || [];

  const renderMenu = (menu) => {
    switch (menu.type) {
      case 'collapse':
        return <NavCollapse key={menu.id} menu={menu} level={1} />;
      case 'item':
        return <NavItem key={menu.id} item={menu} level={1} />;
      case 'empty':
        return <NavItem key={menu.id} item={menu} level={2} />;
      default:
        return (
          <Typography key={menu.id} variant="h6" color="error" align="center">
            Menu Items Error
          </Typography>
        );
    }
  };

  /**
   * A link, wrapped so it can be dragged within its own section.
   *
   * The grip is the only drag handle, exactly as it is for sections: the whole
   * row stays a plain link, so clicking a page never starts a drag by accident.
   * It appears on hover only — a permanent column of grips would add visual
   * weight to every row in a 260px rail to serve an action taken about twice.
   */
  const renderDraggable = (menu) => {
    if (!itemDrag) return renderMenu(menu);
    const dragging = itemDrag.dragId === menu.id;
    const isTarget = itemDrag.overId === menu.id && itemDrag.dragId !== menu.id;
    return (
      <Box
        key={menu.id}
        draggable={dragging}
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          // Firefox refuses to start a drag without data set.
          e.dataTransfer.setData('text/plain', menu.id);
        }}
        onDragOver={(e) => {
          if (!itemDrag.dragId || itemDrag.dragId === menu.id) return;
          e.preventDefault();
          itemDrag.setOverId(menu.id);
        }}
        onDrop={(e) => {
          e.preventDefault();
          itemDrag.onDrop(menu.id);
        }}
        onDragEnd={itemDrag.onDragEnd}
        sx={{
          position: 'relative',
          opacity: dragging ? 0.45 : 1,
          boxShadow: isTarget ? `inset 0 2px 0 ${theme.palette.accent.text}` : 'none',
          borderRadius: '8px',
          '&:hover .cf-item-grip': { opacity: 0.65 }
        }}
      >
        {renderMenu(menu)}
        <Box
          className="cf-item-grip"
          component="span"
          onMouseDown={() => itemDrag.setDragId(menu.id)}
          title="Drag to reorder"
          sx={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            color: 'text.secondary',
            opacity: 0,
            transition: 'opacity .13s ease',
            cursor: 'grab'
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 15 }} />
        </Box>
      </Box>
    );
  };

  const items = childList.map((menu) => renderDraggable(menu));

  // Spacer groups ('emptyAdmin' / 'emptyTrainer') have no title and must not become
  // collapsible headers — they exist only to pad the bottom of the rail.
  if (!collapsible || !item.title) {
    return (
      <>
        <List style={{ color: theme.palette.text.primary }}>{items}</List>
      </>
    );
  }

  const count = childList.length || 0;

  return (
    <Box
      {...(dragHandlers || {})}
      sx={{
        opacity: isDragging ? 0.45 : 1,
        // A line where the section will land, rather than moving everything as you drag.
        boxShadow: isDropTarget ? `inset 0 2px 0 ${theme.palette.accent.text}` : 'none',
        borderRadius: '10px',
        mb: 0.5,
        // An open section sits on a faint panel so its items are visibly ITS items.
        // Without this the header and its links share one flat column and the whole rail
        // reads as a single long list — which is exactly how it was reported.
        backgroundColor: open ? 'rgba(255,255,255,0.045)' : 'transparent',
        overflow: 'hidden'
      }}
    >
      {/* A real button: the sidebar is reachable by keyboard today and collapsing it must
          not change that. aria-expanded is what tells a screen reader the state. */}
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          // Flush left: the section is the outermost level, so it starts at the rail's own
          // edge and its items step in from it.
          pl: 0.5,
          pr: 1,
          py: 1,
          border: 0,
          borderRadius: '10px',
          background: 'transparent',
          font: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          // Bigger than its items (14px vs 13px) and heavier, so the hierarchy is carried
          // by size and indent rather than by case. An earlier pass made this a small
          // uppercase label; larger reads better here because the rail is scanned top-down
          // for a section first and a page second.
          fontSize: '0.875rem',
          fontWeight: 700,
          letterSpacing: '-0.005em',
          color: open ? theme.palette.accent.text : theme.palette.text.primary,
          transition: 'background-color .13s ease, color .13s ease',
          '&:hover': { backgroundColor: theme.palette.surface.hover },
          '&:hover .cf-grip': { opacity: 0.7 },
          '&:focus-visible': { outline: `2px solid ${theme.palette.accent.text}`, outlineOffset: '2px' }
        }}
      >
        <Box
          className="cf-grip"
          component="span"
          // Dragging starts from the grip only, so clicking the header still toggles it.
          onMouseDown={dragHandlers?.onGripMouseDown}
          sx={{ display: 'flex', color: 'text.secondary', opacity: 0, transition: 'opacity .13s ease', cursor: 'grab' }}
          title="Drag to reorder"
        >
          <DragIndicatorIcon sx={{ fontSize: 16 }} />
        </Box>
        <ChevronRightIcon
          sx={{
            fontSize: 18,
            color: 'text.secondary',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform .18s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' }
          }}
        />
        <Typography component="span" sx={{ flex: 1, font: 'inherit', color: 'inherit' }}>
          {item.title}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.625rem',
            letterSpacing: 0,
            color: 'text.secondary',
            backgroundColor: 'rgba(255,255,255,0.08)',
            borderRadius: '999px',
            px: 0.75,
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {count}
        </Typography>
      </Box>

      {/* No left rule: the panel behind an open section already does the containing, and
          a rule as well was one boundary too many in a 260px column. */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List sx={{ py: 0, px: 0.75, pb: 0.75 }}>{items}</List>
      </Collapse>
    </Box>
  );
};

NavGroup.propTypes = {
  item: PropTypes.object,
  orderedChildren: PropTypes.array,
  itemDrag: PropTypes.object,
  open: PropTypes.bool,
  collapsible: PropTypes.bool,
  onToggle: PropTypes.func,
  dragHandlers: PropTypes.object,
  isDragging: PropTypes.bool,
  isDropTarget: PropTypes.bool
};

export default NavGroup;
